import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Newspaper, Briefcase, Tags, Loader2, RefreshCw, Search,
} from 'lucide-react';
import api from '@/api';
import TrendCard from './TrendCard';
import Chip from './Chip';
import CategoryGroupBar from './CategoryGroupBar';
import IndiaBullionHero from './IndiaBullionHero';
import CommoditiesHero from './CommoditiesHero';
import ForexHero from './ForexHero';
import BettingHero from './BettingHero';
import LiveMarketStrip from './LiveMarketStrip';
import { getDefaultRegion, getRegionParam } from '@/utils/region';

const TABS = [
  // Section colour palette + order (May 2026 user spec, iter 55 reorder):
  //   Reseller → cyan   (#06B6D4) — DEFAULT TAB
  //   Investor → yellow (#FACC15)
  //   News     → blue   (#3B82F6)
  { key: 'reseller', label: 'Marketplace seller', Icon: Tags,      color: '#06B6D4', premium: true },
  { key: 'investor', label: 'Investor', Icon: Briefcase, color: '#FACC15', premium: true },
  { key: 'news',     label: 'News',     Icon: Newspaper, color: '#3B82F6', premium: false },
];

const REGIONS = [
  { key: 'all', label: 'All' },
  { key: 'US', label: 'US' },
  { key: 'UK', label: 'UK' },
  { key: 'IN', label: 'India' },
];

const TREND_FILTERS_BY_TAB = {
  news: [],
  investor: [
    { key: 'all', label: 'All' },
    { key: 'buy', label: 'Bullish', color: '#10B981' },
    { key: 'hold', label: 'Neutral', color: '#EAB308' },
    { key: 'sell', label: 'Bearish', color: '#EF4444' },
  ],
  reseller: [
    { key: 'all', label: 'All' },
    { key: 'buy', label: 'Hot', color: '#10B981' },
    { key: 'hold', label: 'Steady', color: '#EAB308' },
    { key: 'sell', label: 'Cooling', color: '#EF4444' },
  ],
};

const CATEGORIES_BY_TAB = {
  news: [
    { key: 'all', label: 'All' },
    { key: 'world_news', label: 'World' },
    { key: 'politics', label: 'Politics' },
    { key: 'technology', label: 'Tech' },
    { key: 'finance', label: 'Finance' },
    { key: 'ai', label: 'AI' },
    { key: 'sports', label: 'Sports' },
    { key: 'entertainment', label: 'Entertainment' },
    { key: 'internet_culture', label: 'Internet' },
    { key: 'economy', label: 'Economy' },
    { key: 'lifestyle', label: 'Lifestyle' },
    { key: 'whats_trending', label: "What's Trending" },
  ],
  investor: [
    { key: 'all', label: 'All' },
    { key: 'crypto', label: 'Crypto' },
    { key: 'stocks', label: 'Stocks' },
    { key: 'commodities', label: 'Commodities' },
    { key: 'precious_metals', label: 'Precious Metals' },
    { key: 'forex', label: 'Forex' },
    { key: 'fantasy_sports', label: 'Fantasy Sports' },
    { key: 'sports_betting', label: 'Betting' },
  ],
  reseller: [
    { key: 'all', label: 'All' },
    { key: 'sneakers', label: 'Sneakers' },
    { key: 'ecommerce', label: 'E-Commerce' },
    { key: 'fashion', label: 'Fashion' },
    { key: 'electronics', label: 'Electronics' },
    { key: 'beauty', label: 'Beauty' },
    { key: 'home', label: 'Home' },
    { key: 'watches', label: 'Watches' },
    { key: 'wellness', label: 'Wellness' },
    { key: 'books', label: 'Books' },
    { key: 'tickets', label: 'Tickets' },
    { key: 'spirits', label: 'Spirits' },
    { key: 'baby_kids', label: 'Baby & Kids' },
    { key: 'auto_parts', label: 'Auto Parts' },
    { key: 'tools_diy', label: 'Tools / DIY' },
    { key: 'gaming', label: 'Gaming' },
    { key: 'trading_cards', label: 'Cards' },
    { key: 'toys', label: 'Toys' },
    { key: 'collectibles', label: 'Collectibles' },
    { key: 'food', label: 'Food' },
  ],
};

// Map source_id → human-readable platform name (for badges)
const PLATFORM_LABELS = {
  tiktok_shop: 'TikTok Shop',
  depop: 'Depop',
  vinted: 'Vinted',
  stockx: 'StockX',
  goat: 'GOAT',
  ebay_browse_api: 'eBay',
  amazon_movers: 'Amazon',
  walmart_bestsellers: 'Walmart',
  etsy_trending: 'Etsy',
  flipkart_trending: 'Flipkart',
  argos_trending: 'Argos',
  vestiaire_collective: 'Vestiaire',
  tcgplayer: 'TCGplayer',
  cardmarket: 'Cardmarket',
  card_ladder: 'Card Ladder',
  steam_community_market: 'Steam',
  sneaker_news: 'Sneaker News',
  highsnobiety: 'Highsnobiety',
  fashionista: 'Fashionista',
  purseblog: 'PurseBlog',
  beckett_news: 'Beckett',
  yahoo_finance_news: 'Yahoo Finance',
  marketwatch_top: 'MarketWatch',
  benzinga: 'Benzinga',
  cointelegraph: 'CoinTelegraph',
  coindesk: 'CoinDesk',
};
const derivePlatform = (src) => {
  if (!src) return 'RSS';
  if (PLATFORM_LABELS[src]) return PLATFORM_LABELS[src];
  if (src.startsWith('google_news_')) return 'Google News';
  return src.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

function FilterRow({ label, children }) {
  return (
    <div className="flex items-center gap-3 mb-3">
      <span className="text-[12px] font-black text-white uppercase tracking-wider w-16 flex-shrink-0"
            style={{ fontFamily: 'Manrope, sans-serif' }}>
        {label}
      </span>
      {/* `min-w-0 flex-1` is critical (May 2026 iter 63): without it the
          flex child expands to its content's intrinsic width, which on the
          Reseller tab's wide CategoryGroupBar (6 meta-group pills + sub-row)
          pushed the page wider than the viewport → Safari auto-zoom kicked in
          and shrank all dashboard content by ~16 %. With `min-w-0`, the
          inner overflow-x:auto can scroll horizontally without inflating
          the page. */}
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

export default function PulseDashboard({ user, onTopicClick, onOpenPortfolio }) {
  const [activeTab, setActiveTab] = useState('reseller');
  const [region, setRegion] = useState(() => getDefaultRegion(user));
  const [trendFilter, setTrendFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [entities, setEntities] = useState([]);
  const [newsTopics, setNewsTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const isSubscribed = user?.subscription_status === 'active' || user?.subscription_status === 'trialing';
  const isPremiumTab = TABS.find(t => t.key === activeTab)?.premium;
  const trendFilters = TREND_FILTERS_BY_TAB[activeTab] || [];
  const categoryOptions = CATEGORIES_BY_TAB[activeTab] || [];

  useEffect(() => { loadData(); }, [activeTab, region, categoryFilter]);

  // Lightweight page-visit tracking — powers admin Users → User Behaviour tile.
  useEffect(() => {
    api.trackPageVisit({ tab: activeTab, category: categoryFilter, region });
  }, [activeTab, categoryFilter, region]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'news') {
        const regionParam = region !== 'all' ? region : undefined;
        // "What's Trending" pill → bare-mention feed (X/Twitter/Google Trends/Wiki);
        // anything else → editorial news only (X-trending noise already excluded server-side).
        const contentType = categoryFilter === 'whats_trending' ? 'trending' : 'news';
        const catParam = categoryFilter === 'whats_trending' || categoryFilter === 'all' ? undefined : categoryFilter;
        const data = await api.getFeed(catParam, regionParam, 50, contentType);
        setNewsTopics(data.topics || []);
        setEntities([]);
      } else if (activeTab === 'reseller') {
        // Unified reseller feed: scored_entities (persona=reseller) ∪ scored_products (TikTok Shop etc.)
        // Fetch server-side category filter so small categories (like Collectibles) aren't truncated by top-50 cutoff.
        const cat = categoryFilter === 'all' ? 'all' : categoryFilter;
        const regionParam = region !== 'all' ? region : undefined;
        const [entRes, productRes] = await Promise.allSettled([
          api.getTrendsFeed('reseller', cat, { limit: 50, region: regionParam }),
          api.getPulseTraders({ limit: 50, category: cat === 'all' ? undefined : cat, region: regionParam }),
        ]);
        const entityItems = entRes.status === 'fulfilled' ? (entRes.value.entities || []) : [];
        const productItems = productRes.status === 'fulfilled' ? (productRes.value.products || []) : [];
        const productsAsEntities = productItems.map(p => ({
          canonical_id: p.id,
          entity_name: p.product,
          category: p.category,
          hotness_score: p.sell_score || 0,
          trend_level: p.sell_score >= 75 ? 'on_fire' : p.sell_score >= 50 ? 'heating_up' : p.sell_score >= 25 ? 'warming' : 'watching',
          direction_consensus: p.decision === 'SELL NOW' ? 'strongly_bullish' : p.decision === 'AVOID' ? 'bearish' : 'neutral',
          headline: p.card_text?.ai_insight || `${p.product} — ${p.decision}`,
          buy_sell: { reseller: { action: p.decision === 'SELL NOW' ? 'strong_buy' : p.decision === 'AVOID' ? 'sell' : 'hold', score: p.sell_score } },
          insight_reseller: p.card_text?.content_angle || '',
          why_trending: p.card_text?.geo_play || '',
          score_breakdown: {},
          source_count: 1,
          platform: 'TikTok Shop',
          top_sources: ['tiktok_shop'],
          // Projection Engine v2 (May 2026) — backend (routes/pulse.py /traders)
          // augments each product with analyst_consensus + projection_detail so
          // the TrendCard's Projection row renders inline without refetch.
          analyst_consensus: p.analyst_consensus,
          _projection_detail: p.projection_detail,
        }));
        const entityItemsWithPlatform = entityItems.map(e => ({
          ...e,
          platform: derivePlatform((e.top_sources || [])[0]),
        }));
        // Merge + sort by score desc
        const merged = [...entityItemsWithPlatform, ...productsAsEntities]
          .sort((a, b) => (b.hotness_score || 0) - (a.hotness_score || 0));
        setEntities(merged);
        setNewsTopics([]);
      } else {
        // investor — server-side category filter
        const cat = categoryFilter === 'all' ? 'all' : categoryFilter;
        const regionParam = region !== 'all' ? region : undefined;
        const data = await api.getTrendsFeed('investor', cat, { limit: 50, region: regionParam });
        const list = (data.entities || []).map(e => ({
          ...e,
          platform: derivePlatform((e.top_sources || [])[0]),
        }));
        setEntities(list);
        setNewsTopics([]);
      }
    } catch (e) {
      console.error('Dashboard load failed:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  const handleSubscribe = async () => {
    try {
      const res = await api.createCheckout(window.location.origin);
      if (res.url) window.location.href = res.url;
    } catch (e) {
      console.error('Checkout failed:', e);
    }
  };

  // Convert news topics to entity-like shape
  const newsAsEntities = newsTopics.map(t => ({
    canonical_id: t.id,
    entity_name: t.title,
    category: t.category,
    hotness_score: t.trend_score || 50,
    trend_level: t.trend_score >= 85 ? 'on_fire' : t.trend_score >= 70 ? 'heating_up' : t.trend_score >= 50 ? 'warming' : 'watching',
    direction_consensus: 'neutral',
    headline: t.headline || t.title,
    card_hook: t.card_hook,
    image_url: t.image_url || '',
    buy_sell: {},
    source_url: t.source_url || t.raw_data?.link || t.raw_data?.url || null,
    why_trending: t.source_label || t.source || '',
    score_breakdown: {},
    source_count: t.source_count || 1,
  }));

  // Raw items
  const rawItems = activeTab === 'news' ? newsAsEntities : entities;

  // Apply trend filter (premium tabs only)
  const trendFiltered = useMemo(() => {
    if (trendFilter === 'all') return rawItems;
    return rawItems.filter(e => {
      const persona = activeTab === 'investor' ? 'investor' : 'reseller';
      const a = e.buy_sell?.[persona]?.action || '';
      if (trendFilter === 'buy') return a.includes('buy');
      if (trendFilter === 'sell') return a.includes('sell');
      if (trendFilter === 'hold') return a === 'hold';
      return true;
    });
  }, [rawItems, trendFilter, activeTab]);

  // Apply category filter
  const categoryFiltered = useMemo(() => {
    // 'all' and 'whats_trending' are server-side filters (already applied via the
    // /api/feed content_type param in loadData), so don't re-filter client-side.
    // Any topic returned by the server is already correctly scoped — applying a
    // client-side `category === 'whats_trending'` check would always return 0
    // because real topic categories are 'internet_culture', 'world_news', etc.
    if (categoryFilter === 'all' || categoryFilter === 'whats_trending') return trendFiltered;
    return trendFiltered.filter(e => (e.category || '').toLowerCase() === categoryFilter);
  }, [trendFiltered, categoryFilter]);

  // Apply search
  const displayItems = useMemo(() => {
    if (!searchQuery.trim()) return categoryFiltered;
    const q = searchQuery.toLowerCase();
    return categoryFiltered.filter(e =>
      (e.entity_name || '').toLowerCase().includes(q) ||
      (e.headline || '').toLowerCase().includes(q) ||
      (e.category || '').toLowerCase().includes(q)
    );
  }, [categoryFiltered, searchQuery]);

  // Premium gating: 1 free card per unique category
  const freeIds = useMemo(() => {
    if (!isPremiumTab || isSubscribed) return new Set();
    const seen = new Set();
    const free = new Set();
    for (const item of displayItems) {
      const cat = item.category || 'unknown';
      if (!seen.has(cat)) {
        seen.add(cat);
        free.add(item.canonical_id || item.id);
      }
    }
    return free;
  }, [displayItems, isPremiumTab, isSubscribed]);

  const tabColor = TABS.find(t => t.key === activeTab)?.color || '#fff';

  return (
    <div data-testid="pulse-dashboard" className="min-h-screen pb-28 pt-4 px-4" style={{ fontFamily: 'Manrope, sans-serif' }}>

      {/* Ambient glow */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden -z-10">
        <div className="absolute w-72 h-72 rounded-full blur-[100px] opacity-10" style={{ top: '-5%', left: '-10%', background: tabColor }} />
      </div>

      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between mb-4">
        <h1 className="text-3xl font-black text-white tracking-tight" style={{ fontFamily: 'Unbounded, sans-serif' }}>
          Dashboard
        </h1>
        <div className="flex items-center gap-2">
          {onOpenPortfolio && (
            <button
              data-testid="open-portfolio-btn"
              onClick={onOpenPortfolio}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold text-black transition-all hover:-translate-y-0.5 active:scale-95"
              style={{ background: 'linear-gradient(90deg, #10B981, #34D399)', fontFamily: 'Manrope, sans-serif', boxShadow: '0 4px 12px rgba(16,185,129,0.25)' }}
            >
              <Briefcase size={12} />
              My Portfolio
            </button>
          )}
          <button data-testid="dashboard-refresh" onClick={handleRefresh}
            className="p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors">
            <RefreshCw size={14} className={`text-gray-400 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </motion.div>

      {/* Sticky filters + search — stays visible on scroll */}
      <div
        data-testid="dashboard-sticky-controls"
        className="sticky top-0 z-30 -mx-4 px-4 pt-1 pb-3"
        style={{
          background: 'rgba(13,13,13,0.92)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      >
      {/* Row 1: Tabs — bigger, bolder, coloured outlines per section
          (May 2026: News=blue, Investor=yellow, Reseller=cyan) */}
      <div data-testid="dashboard-tabs" className="flex gap-2 mb-4">
        {TABS.map(tab => {
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              data-testid={`tab-${tab.key}`}
              onClick={() => { setActiveTab(tab.key); setTrendFilter('all'); setCategoryFilter('all'); setSearchQuery(''); }}
              className="relative flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl transition-all"
              style={{
                background: isActive ? `${tab.color}25` : 'rgba(255,255,255,0.03)',
                border: `2px solid ${isActive ? tab.color : tab.color + '60'}`,
                boxShadow: isActive ? `0 0 0 1px ${tab.color}30 inset` : 'none',
              }}
            >
              <tab.Icon size={20} style={{ color: tab.color }} strokeWidth={2.5} />
              <span
                className="text-base font-black uppercase tracking-tight"
                style={{ color: isActive ? '#fff' : tab.color, fontFamily: 'Manrope, sans-serif' }}
              >
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Row 2: Region */}
      <FilterRow label="Region">
        <div data-testid="region-filter" className="flex gap-1.5">
          {REGIONS.map(r => (
            <Chip key={r.key} testId={`region-${r.key}`} active={region === r.key}
              label={r.label} onClick={() => setRegion(r.key)} />
          ))}
        </div>
      </FilterRow>

      {/* Row 3: Trend (premium tabs only) */}
      {trendFilters.length > 0 && (
        <FilterRow label="Trend">
          <div data-testid="trend-filter" className="flex gap-1.5">
            {trendFilters.map(tf => (
              <Chip key={tf.key} testId={`trend-filter-${tf.key}`} active={trendFilter === tf.key}
                label={tf.label} color={tf.color} onClick={() => setTrendFilter(tf.key)} />
            ))}
          </div>
        </FilterRow>
      )}

      {/* Row 4: Category — grouped meta-sections only on Reseller tab (19 pills) */}
      {categoryOptions.length > 0 && (
        <FilterRow label="Category">
          <CategoryGroupBar
            categories={categoryOptions}
            activeCategory={categoryFilter}
            onCategoryChange={setCategoryFilter}
            grouped={activeTab === 'reseller'}
          />
        </FilterRow>
      )}

      {/* Search bar — high-contrast: thick border in the active tab colour,
          bigger placeholder + value text, glowing focus ring (May 2026) */}
      <div data-testid="search-bar" className="relative mt-2">
        <Search size={20} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: tabColor }} strokeWidth={2.75} />
        <input
          data-testid="search-input"
          data-custom-border=""
          type="text"
          placeholder={`Search ${TABS.find(t => t.key === activeTab)?.label || ''}...`}
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="w-full pl-12 pr-3 py-3.5 rounded-xl text-base font-bold text-white placeholder-white/60 outline-none transition-all"
          style={{
            background: '#0F0F0F',
            border: `2px solid ${tabColor}80`,
            boxShadow: `0 0 0 1px ${tabColor}25 inset, 0 4px 16px ${tabColor}15`,
            fontFamily: 'Manrope, sans-serif',
            color: '#fff',
          }}
        />
      </div>
      </div>
      {/* end sticky controls */}

      <div className="mt-4" />

      {/* Pinned pill heroes — one per Investor sub-category */}
      {/* Pinned pill heroes — one per Investor sub-category.
          IndiaBullionHero is region-scoped: India shows it; UK/US users see
          their own market data instead via the standard category cards. */}
      {activeTab === 'investor' && categoryFilter === 'precious_metals' && (region === 'IN' || region === 'all') && <IndiaBullionHero />}
      {activeTab === 'investor' && categoryFilter === 'commodities' && <CommoditiesHero />}
      {activeTab === 'investor' && categoryFilter === 'forex' && <ForexHero region={region} />}
      {activeTab === 'investor' && categoryFilter === 'sports_betting' && <BettingHero />}

      {/* Live markets strip — only on the "All" view of the Investor tab.
          Surfaces real-time prices + 7-day sparklines from CoinGecko/Polygon/Alpha Vantage
          so users see actual market motion alongside the AI-scored entity grid. */}
      {activeTab === 'investor' && categoryFilter === 'all' && <LiveMarketStrip />}

      {/* Content — 2-column grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-gray-600" size={32} />
        </div>
      ) : displayItems.length === 0 ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-16">
          <p className="text-gray-400 text-sm font-medium">
            {searchQuery ? `No results for "${searchQuery}"` : 'No data for this filter yet.'}
          </p>
          <p className="text-gray-600 text-xs mt-1">Try a different filter or check back later.</p>
        </motion.div>
      ) : (
        <AnimatePresence mode="wait">
          <motion.div
            key={`${activeTab}-${region}-${trendFilter}-${categoryFilter}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            // -mx-4 px-1 escapes PulseDashboard's px-4 (only remaining inset
            // since .app-shell-flush removes the parent's 16px). 4-px padding
            // keeps card outlines from clipping the edge (May 2026 iter 62).
            className="grid grid-cols-1 gap-3 -mx-4 px-1"
          >
            {displayItems.map((entity, i) => {
              const isBlurred = isPremiumTab && !isSubscribed && !freeIds.has(entity.canonical_id || entity.id);
              return (
                <TrendCard
                  key={entity.canonical_id || i}
                  entity={entity}
                  persona={activeTab === 'reseller' ? 'reseller' : 'investor'}
                  index={i}
                  isNews={activeTab === 'news'}
                  tabContext={activeTab}
                  blurred={isBlurred}
                  borderColor={tabColor}
                  onSubscribe={handleSubscribe}
                  onClick={!isBlurred && onTopicClick ? () => onTopicClick({
                    id: entity.canonical_id || entity.id,
                    title: entity.entity_name,
                    category: entity.category,
                    trend_score: entity.hotness_score,
                    source_url: entity.source_url,
                    headline: entity.headline,
                    image_url: entity.image_url,
                  }) : undefined}
                />
              );
            })}
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
}
